import { useMemo, useRef, useState } from 'react';

import { useFormikContext } from 'formik';
import { get, camelCase } from 'lodash';
import { Col, Form } from 'react-bootstrap';

import type { IForm, IFormField } from '@customTypes/form';
import { camelToTitleCase } from '@helpers/string';
import AddressAutoComplete, { AddressAutoCompleteMethods } from './AddressAutoComplete';

const generateName = (name: string, prefix = ''): string => {
  return prefix ? `${prefix}.${name}` : name;
};

export default function FormField<T extends IForm>({
  type = 'text',
  prefix = '',
  name,
  cols,
  className = '',
  label,
  disabled = false,
  required = false,
  helperText = '',
  address = false,
  inputProps = {},
}: IFormField<T>) {
  const { touched, errors, values, handleBlur, handleChange, setFieldValue, validateForm } = useFormikContext<T>();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const groupClassName = useMemo(() => {
    if (cols === 4) {
      return 'col-12 col-lg-4';
    }
    if (cols === 8) {
      return 'col-12 col-lg-8';
    }

    return className;
  }, [cols, className]);
  const formControlName = useMemo(() => generateName(name, prefix), [prefix, name]);
  const error = useMemo(() => get(errors, formControlName), [formControlName, errors]);
  const isDirty = useMemo(() => get(touched, formControlName), [formControlName, touched]);
  const fieldValue = useMemo(() => get(values, formControlName), [values, formControlName]);

  // for address
  const autocompleteRef = useRef<AddressAutoCompleteMethods>(null);

  const handleAddressChange = (street: string, city?: string, region?: string, postal?: string) => {
    setFieldValue(formControlName, street);
    setFieldValue(generateName('address.city', prefix), city ?? '');
    setFieldValue(generateName('address.region', prefix), region ?? '');
    setFieldValue(generateName('address.postal_code', prefix), postal ?? '');

    setTimeout(() => validateForm(), 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (address) {
      let processed = false;
      if (e.key === 'ArrowDown') {
        autocompleteRef.current?.onArrowKey(1);
        processed = true;
      } else if (e.key === 'ArrowUp') {
        autocompleteRef.current?.onArrowKey(-1);
        processed = true;
      } else if (e.key === 'Enter') {
        autocompleteRef.current?.select();
        processed = true;
      }

      if (processed) {
        e.stopPropagation();
        e.preventDefault();
      }
    }
  };

  return (
    <Form.Group as={Col} controlId={`input_${formControlName}`} className={groupClassName}>
      <Form.Label className={required ? 'required' : ''}>{label ?? camelToTitleCase(camelCase(name))}</Form.Label>
      <div className='position-relative'>
        <Form.Control
          ref={inputRef}
          type={type}
          name={formControlName}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            setTimeout(() => setIsFocused(false));
            handleBlur(e);
          }}
          onFocus={() => setIsFocused(true)}
          onChange={handleChange}
          value={fieldValue ?? ''}
          disabled={disabled}
          aria-describedby={`input_${formControlName}Desc`}
          isValid={isDirty && !error}
          isInvalid={isDirty && !!error}
          onKeyDown={handleKeyDown}
          {...(address && { autoComplete: 'off' })}
          {...inputProps}
        />
        {address && (
          <AddressAutoComplete
            ref={autocompleteRef}
            focused={isFocused}
            value={fieldValue}
            onChange={handleAddressChange}
          />
        )}
      </div>
      {!!helperText && <Form.Text id={`input_${formControlName}Desc`}>{helperText}</Form.Text>}
      {!!error && (
        <Form.Control.Feedback as='small' role='alert' type='invalid'>
          {error as string}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
}
